import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";

import { Save, Plus, Tag, Box, BadgeDollarSign, Archive, Camera, Upload, Trash, Image as ImageIcon, Copy, Key, Link as LinkIcon, RefreshCcw, Search, X, Image, Trash2, Info, Boxes, Layers } from "lucide-react";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { IconTextButton, IconButton } from "../../../components/ButtonComponents";
import { ETabs } from "../../../components/ETabs";
import { ProductComponentsTab, ComponentItemForForm } from "./ProductComponentsTab";

import { Product, ProductVariantForForm } from "../../../types/product";
import { uploadImageToServer, deleteImageFromServer } from "../../../service/stockImageService";
import { useUnitStore } from "../../../store/unitStore";
import { useCategoryStore } from "../../../store/categoryStore";
import { useBrandStore } from "../../../store/brandStore";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { toast } from "react-toastify";
import { Filter } from "../../../types";
import { Category } from "../../../types/category";
import { useLightbox } from "../../../hooks/useLightbox";
import { ImageLightbox } from "../../../components/ImageLightbox";
import api from "../../../service/api";

interface ProductImage {
  id: number;
  url: string;
}

interface PriceState {
  listCode: number;
  listName: string;
  price: number;
}

type ProductFormState = Omit<Product,
  'id' | 'createdAt' | 'createdById' | 'images' | 'variants' | 'prices' | 'categories' | 'components'
> & {
  variants: ProductVariantForForm[];
  images: ProductImage[];
  prices: PriceState[];
  categoryIds: number[];
  selectedCategories: Category[];
  brandId: number | null;
  brandName: string;
  components: ComponentItemForForm[];
};

interface ProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onSave: (data: any) => void;
}

const initialData: ProductFormState = {
  name: "",
  sku: "",
  barcode: "",
  description: "",
  quantity: 0,
  unitId: null,
  taxRate: 20,
  taxIncluded: false,
  shelfLocation: "",
  volume: 1,
  weight: 1,
  desi: 0,
  desiMultiplier: null,
  purchaseCurrency: "TRY",
  source: "APP",
  externalId: "",
  categoryId: null,
  categoryIds: [],
  selectedCategories: [],
  brandId: null,
  brandName: "",
  variants: [],
  images: [],
  components: [],
  prices: [
    { listCode: 1, listName: "Perakende Satış Fiyatı", price: 0 },
    { listCode: 2, listName: "Toptan Satış Fiyatı", price: 0 },
    { listCode: 3, listName: "Ara Fiyat", price: 0 },
    { listCode: 4, listName: "Dip Fiyat", price: 0 },
    { listCode: 5, listName: "Maliyet", price: 0 },
  ],
};

const createNewVariant = (index: number): ProductVariantForForm => ({
  id: `new-${index}`,
  name: '',
  sku: '',
  barcode: '',
  quantity: 0,
});

export const ProductDialog: React.FC<ProductDialogProps> = ({
  isOpen,
  onClose,
  product,
  onSave,
}) => {
  const [formData, setFormData] = useState<ProductFormState>(initialData);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Lightbox Hook
  const {
    isOpen: isLightboxOpen,
    images: lightboxImages,
    currentIndex,
    setCurrentIndex,
    openLightbox,
    closeLightbox
  } = useLightbox();

  const { data: units, fetchDataDynamic: fetchUnits } = useUnitStore();
  const { fetchDataDynamic: fetchCategories } = useCategoryStore();
  const { fetchDataDynamic: fetchBrands, loading: brandLoading } = useBrandStore();
  const [lastLoadedProductId, setLastLoadedProductId] = useState<number | null | 'new'>(null);
  const categorySelect = useSelectDialog<Category>();
  const brandSelect = useSelectDialog<any>();


  // Birim Döngüsünü Engelleyen useEffect
  useEffect(() => {
    if (isOpen && units.length === 0) {
      fetchUnits();
    }
  }, [isOpen, units.length, fetchUnits]);

  // Form Verilerini Dolduran useEffect
  useEffect(() => {
    // Dialog kapalıysa state'i sıfırla ve takibi temizle
    if (!isOpen) {
      setLastLoadedProductId(null);
      return;
    }


    // Mevcut yüklü ürünle gelen ürün aynıysa (veya ikisi de yeni ürünse) tekrar doldurma!
    const currentId = product?.id || 'new';
    if (lastLoadedProductId === currentId) return;

    // Verileri doldur
    if (product) {
      const productCategories = product.categories || [];
      setFormData({
        ...initialData,
        ...product,
        categoryIds: productCategories.map(c => c.id),
        selectedCategories: productCategories,
        brandName: (product as any).brand?.name || "",
        variants: product.variants?.map(v => ({
          id: v.id,
          name: v.name,
          sku: v.sku,
          barcode: v.barcode,
          quantity: v.quantity,
        })) ?? [],
        images: product.images?.map(img => ({ id: img.id, url: img.url })) ?? [],
        components: (product.components || []).map((comp: any) => ({
          id: comp.id,
          stockId: comp.stockId,
          firmId: comp.firmId,
          name: comp.name || "",
          weight: comp.weight ?? 0,
          quantity: comp.quantity ?? 1,
          locations: comp.locations || "",
          imageUrl: comp.imageUrl || null,
          secondUnitName: comp.secondUnitName || null,
          secondUnitMultiplier: comp.secondUnitMultiplier ?? null,
          createdAt: comp.createdAt,
          updatedAt: comp.updatedAt,
        })),
        prices: product.prices?.length
          ? product.prices.map(p => ({ listCode: p.listCode, listName: p.listName || "", price: p.price }))
          : initialData.prices
      });
    } else {
      setFormData(initialData);
    }

    // Bu ürünün yüklendiğini işaretle
    setLastLoadedProductId(currentId);
    setActiveIndex(0);
  }, [isOpen, product, lastLoadedProductId]);

  const openCategorySelect = async () => {
    const res = await fetchCategories({ pageIndex: 1, pageSize: 10 });
    const selectedList = await categorySelect.open({
      title: "Kategori Seç",
      multiple: true,
      initialSelection: formData.selectedCategories,
      data: res.items || [],
      columns: [
        { field: "name", header: "Kategori Adı" },
        { field: "parent.name", header: "Üst Kategori" }
      ],
      onFilter: async (p) => {
        const r = await fetchCategories({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter as Filter });
        return { data: r.items, total: r.total };
      }
    });

    if (selectedList && Array.isArray(selectedList)) {
      setFormData(p => ({
        ...p,
        categoryIds: selectedList.map(c => c.id),
        selectedCategories: selectedList
      }));
    }
  };

  const removeCategory = (id: number) => {
    setFormData(p => ({
      ...p,
      categoryIds: p.categoryIds.filter(cid => cid !== id),
      selectedCategories: p.selectedCategories.filter(c => c.id !== id)
    }));
  };

  const openBrandSelect = async () => {
    const res = await fetchBrands({ pageIndex: 1, pageSize: 10 });
    const selected = await brandSelect.open({
      title: "Marka Seç",
      data: res.items || [],
      loading: brandLoading,
      columns: [
        { field: "name", header: "Marka Adı" },
        { field: "externalId", header: "Dış Kod" }
      ],
      onFilter: async (p) => {
        const r = await fetchBrands({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter as Filter });
        return { data: r.items, total: r.total };
      }
    });
    if (selected) setFormData({ ...formData, brandId: selected.id, brandName: selected.name });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !product?.id) {
      if (!product?.id) toast.warning("Lütfen önce ürünü kaydedin.");
      return;
    }
    setIsUploading(true);
    try {
      const result = await uploadImageToServer(file, product.id, formData.images.length === 0);
      setFormData(p => ({ ...p, images: [...p.images, result] }));
      toast.success("Görsel yüklendi.");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = async (index: number) => {
    const img = formData.images[index];
    if (await deleteImageFromServer(img.id)) {
      setFormData(p => ({ ...p, images: p.images.filter((_, i) => i !== index) }));
    }
  };

  const handleVariantChange = (index: number, field: keyof ProductVariantForForm, value: any) => {
    setFormData(prev => {
      const newVariants = [...prev.variants];
      newVariants[index] = { ...newVariants[index], [field]: value };
      return { ...prev, variants: newVariants };
    });
  };
  const handlePriceChange = (index: number, value: number) => {
    setFormData(prev => {
      const newPrices = [...prev.prices];
      newPrices[index] = { ...newPrices[index], price: value };
      return { ...prev, prices: newPrices };
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;

    // Backend'in beklemediği ve hata veren excess (fazlalık) property'leri formData'dan ayırıyoruz.
    // Aynı zamanda description alanının null gitmemesini sağlıyoruz.
    const {
      rolls,
      stockLocations,
      selectedCategories,
      brandName,
      secondUnitName,
      secondUnitMultiplier,
      ...cleanFormData
    } = formData as any;

    delete cleanFormData.secondUnitName;
    delete cleanFormData.secondUnitMultiplier;
    delete cleanFormData.rolls;
    delete cleanFormData.stockLocations;

    const payload = {
      ...cleanFormData,
      components: (formData.components || []).map((c: any) => ({
        ...(typeof c.id === 'number' ? { id: c.id } : {}),
        name: c.name || "",
        quantity: c.quantity ?? 1,
        weight: c.weight ?? 0,
        locations: c.locations || "",
        imageUrl: c.imageUrl || null,
        secondUnitName: c.secondUnitName || null,
        secondUnitMultiplier: c.secondUnitMultiplier ?? null,
      })),
      description: cleanFormData.description ?? "",
      categoryIds: formData.categoryIds,
      categoryId: formData.categoryIds?.[0] || formData.categoryId || null,
      images: formData.images?.map((img: any, index: number) => ({
        id: img.id,
        url: img.url,
        isMain: img.isMain ?? (index === 0),
        sortOrder: img.sortOrder ?? index
      })),
      variants: formData.variants.map(({ id, ...rest }) => ({
        ...(typeof id === 'number' ? { id } : {}),
        ...rest,
        barcode: rest.barcode || "",
        sku: rest.sku || ""
      }))
    };

    onSave(payload);
    onClose();
  };

  const tabs = useMemo(() => [
    {
      id: 'basic-info',
      header: <><Box className="w-4 h-4 mr-2" /> Temel Bilgiler</>,
      content: (
        <div className="space-y-4 p-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">Ürün Adı *</label>
              <InputText value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">SKU / Stok Kodu</label>
              <InputText value={formData.sku ?? ''} onChange={(e) => setFormData(p => ({ ...p, sku: e.target.value }))} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <CustomSelectInput
                label={<><Tag size={14} /> Kategoriler</>}
                value={
                  formData.selectedCategories.length > 0 ? (
                    <div className="flex items-center gap-2 w-full">
                      <span className="bg-indigo-600 text-white px-2 py-0.5 rounded text-[11px] font-medium flex items-center gap-1 shrink-0">
                        {formData.selectedCategories[0].name}
                        <X size={12} className="hover:text-red-200 cursor-pointer" onClick={(e) => { e.stopPropagation(); removeCategory(formData.selectedCategories[0].id); }} />
                      </span>
                      {formData.selectedCategories.length > 1 && (
                        <span className="text-xs font-semibold">+ {formData.selectedCategories.length - 1} kategori daha</span>
                      )}
                    </div>
                  ) : ""
                }
                placeholder="Kategori Seç..."
                icon={Search}
                onClick={openCategorySelect}
                onClear={formData.selectedCategories.length > 0 ? () => setFormData(p => ({ ...p, selectedCategories: [] })) : undefined}
              />
            </div>

            <div className="flex flex-col gap-2">
              <CustomSelectInput
                label={<><BadgeDollarSign size={14} /> Marka</>}
                value={formData.brandName || ""}
                placeholder="Marka Seç..."
                icon={Search}
                onClick={openBrandSelect}
                onClear={formData.brandId ? () => setFormData(p => ({ ...p, brandId: null, brandName: "" })) : undefined}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">Birim</label>
              <Dropdown value={formData.unitId} options={units} optionLabel="name" optionValue="id" onChange={(e) => setFormData(p => ({ ...p, unitId: e.value }))} placeholder="Seçiniz" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">KDV Oranı (%)</label>
              <InputNumber value={formData.taxRate} onValueChange={(e) => setFormData(p => ({ ...p, taxRate: e.value ?? 20 }))} suffix=" %" showButtons buttonLayout="horizontal" />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">Maliyet Döviz</label>
              <Dropdown value={formData.purchaseCurrency} options={['TRY', 'USD', 'EUR', 'GBP']} onChange={(e) => setFormData(p => ({ ...p, purchaseCurrency: e.value }))} />
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'variants',
      header: <><Tag className="w-4 h-4 mr-2" /> Varyantlar</>,
      content: (
        <div className="space-y-4 p-2">
          <div className="flex justify-between items-center">
            <h3 className="font-bold">Fiziksel Varyantlar</h3>
            <IconTextButton onClick={() => setFormData(p => ({ ...p, variants: [...p.variants, createNewVariant(p.variants.length)] }))} icon={Plus} className="bg-indigo-600 text-white btn-sm">Varyant Ekle</IconTextButton>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-3">
            {formData.variants.length === 0 ? (
              <div className="text-center py-8 opacity-50"><p>Henüz varyant eklenmemiş.</p></div>
            ) : (
              formData.variants.map((v, i) => (
                <div key={v.id} className="grid grid-cols-12 gap-3 p-3 card2 relative">
                  <div className="col-span-6 flex flex-col gap-1">
                    <label className="text-[10px] uppercase font-bold">Varyant Tanımı</label>
                    <InputText value={v.name} onChange={(e) => handleVariantChange(i, 'name', e.target.value)} placeholder="Mavi / XL" />
                  </div>
                  <div className="col-span-5 flex flex-col gap-1">
                    <label className="text-[10px] uppercase font-bold">Varyant SKU</label>
                    <InputText value={v.sku ?? ''} onChange={(e) => handleVariantChange(i, 'sku', e.target.value)} />
                  </div>
                  <div className="col-span-1 flex items-end justify-center">
                    <IconButton onClick={() => setFormData(p => ({ ...p, variants: p.variants.filter((_, idx) => idx !== i) }))} icon={Trash2} className="text-red-400 hover:text-red-600" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )
    },
    {
      id: 'prices',
      header: <><BadgeDollarSign className="w-4 h-4 mr-2" /> Fiyat Listeleri</>,
      content: (
        <div className="space-y-4 p-2">
          {formData.prices.map((p, i) => (
            <div key={p.listCode} className="flex items-center justify-between p-2 card2 gap-4">
              <div className="flex-1">
                <div className="font-bold">{p.listName}</div>
                <div className="text-[10px] opacity-60">CODE: {p.listCode}</div>
              </div>
              <InputNumber
                value={p.price} style={{ minWidth: "300px" }}
                className="justify-center"
                onValueChange={(e) => handlePriceChange(i, e.value || 0)}
                showButtons buttonLayout="horizontal" mode="currency" currency={formData.purchaseCurrency} locale="tr-TR"
              />
            </div>
          ))}
        </div>
      )
    },
    {
      id: 'components',
      header: (
        <>
          <Boxes className="w-4 h-4 mr-2" />
          Bileşenler (Set)
          {formData.components && formData.components.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-primary text-white font-bold leading-none">
              {formData.components.length}
            </span>
          )}
        </>
      ),
      content: (
        <ProductComponentsTab
          components={formData.components}
          productName={formData.name}
          defaultShelfLocation={formData.shelfLocation ?? ""}
          productId={product?.id}
          onChange={(comps) => setFormData(p => ({ ...p, components: comps }))}
          onApplyTotalWeight={(totWeight) => setFormData(p => ({ ...p, weight: totWeight }))}
          onOpenLightbox={openLightbox}
        />
      )
    },
    {
      id: 'logistics',
      header: <><Box className="w-4 h-4 mr-2" /> Depo & Lojistik</>,
      content: (
        <div className="space-y-4 p-2">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">Raf Adresi / Konum</label>
              <InputText value={formData.shelfLocation ?? ''} onChange={(e) => setFormData(p => ({ ...p, shelfLocation: e.target.value }))} placeholder="A-12-04" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">Hacim (m³)</label>
              <InputNumber value={formData.volume} onValueChange={(e) => setFormData(p => ({ ...p, volume: e.value ?? 1 }))} minFractionDigits={2} showButtons buttonLayout="horizontal" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">Ağırlık (Gr)</label>
              <InputNumber value={formData.weight} onValueChange={(e) => setFormData(p => ({ ...p, weight: e.value ?? 1 }))} minFractionDigits={2} showButtons buttonLayout="horizontal" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm">Sabit Desi</label>
              <InputNumber value={formData.desi ?? 0} onValueChange={(e) => setFormData(p => ({ ...p, desi: e.value ?? 0 }))} minFractionDigits={2} showButtons buttonLayout="horizontal" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="font-semibold text-sm flex items-center gap-1">
                İç İçe Geçme Desi Çarpanı
                <Info size={14} className="text-primary desi-info-tooltip cursor-help" />
                <Tooltip target=".desi-info-tooltip" position="top">
                  <div className="text-xs max-w-xs">
                    Örn: 1 desilik bir kova var ve 10 adet sipariş edildi. Desi çarpanı 0,2 ise hesaplama: 1 + (9 * 0,2) = 2,8 desi. Sıfır veya boş bırakılırsa normal adet ile çarpılır.
                  </div>
                </Tooltip>
              </label>
              <InputNumber value={formData.desiMultiplier ?? null} onValueChange={(e) => setFormData(p => ({ ...p, desiMultiplier: e.value }))} minFractionDigits={2} showButtons buttonLayout="horizontal" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-semibold text-sm">Ürün Açıklaması</label>
            <InputTextarea value={formData.description ?? ''} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} rows={3} autoResize />
          </div>
        </div>
      )
    },
    {
      id: 'images',
      header: <><Image className="w-4 h-4 mr-2" /> Görseller</>,
      content: (
        <div className="p-2 space-y-6">
          <div className="flex justify-between items-center border-b pb-3">
            <div>
              <h3 className="font-bold text-lg">Ürün Galerisi</h3>
              <p className="text-xs text-gray-500">Görseller e-ticaret vitrininizde bu sırada sergilenecektir.</p>
            </div>
            <label className={`cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 text-sm font-medium transition-all shadow-sm ${isUploading || !product?.id ? 'opacity-50 cursor-not-allowed' : ''}`}>
              {isUploading ? <i className="pi pi-spin pi-spinner" /> : <Plus size={18} />}
              {isUploading ? 'Yükleniyor...' : 'Yeni Görsel Yükle'}
              <input type="file" className="hidden" onChange={handleFileSelect} disabled={isUploading || !product?.id} />
            </label>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {formData.images.map((img, i) => (
              <div
                key={img.id}
                className="group relative aspect-square rounded-xl overflow-hidden border-2 border-transparent hover:border-indigo-500 bg-gray-50 transition-all cursor-pointer shadow-sm hover:shadow-md"
                onClick={() => openLightbox(formData.images.map(image => image.url), i)}
              >
                <img src={img.url} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" alt={`Ürün ${i}`} />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={(e) => { e.stopPropagation(); openLightbox(formData.images.map(image => image.url), i); }} className="p-2 bg-white rounded-full text-gray-700 hover:bg-indigo-500 hover:text-white transition-colors" title="Büyüt">
                    <Search size={16} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleRemoveImage(i); }} className="p-2 bg-white rounded-full text-red-500 hover:bg-red-500 hover:text-white transition-colors" title="Sil">
                    <Trash2 size={16} />
                  </button>
                </div>
                {i === 0 && <div className="absolute top-2 left-2 bg-indigo-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold shadow-lg">ANA GÖRSEL</div>}
              </div>
            ))}
            {formData.images.length === 0 && !isUploading && (
              <div className="col-span-full py-12 flex flex-col items-center justify-center border-2 border-dashed rounded-2xl">
                <Image size={48} strokeWidth={1} className="mb-2" />
                <p className="text-sm font-medium">Henüz bir görsel yüklenmedi</p>
              </div>
            )}
          </div>
        </div>
      )
    }
  ], [formData, units, isUploading, product?.id, openLightbox]);

  return (
    <>
      <Dialog
        header={product ? "Ürün Düzenle" : "Yeni Ürün Ekle"}
        maximizable maximized resizable={false} visible={isOpen} onHide={onClose} modal className="w-full lg:w-8/12"
        footer={
          <div className="flex justify-end gap-2">
            <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
            <IconTextButton onClick={handleSubmit} icon={Save} className="btn-new">Kaydet</IconTextButton>
          </div>
        }
      >
        <ETabs tabs={tabs} initialActiveIndex={activeIndex} onTabChange={setActiveIndex} />
      </Dialog>

      <ImageLightbox
        isOpen={isLightboxOpen}
        images={lightboxImages}
        currentIndex={currentIndex}
        onIndexChange={setCurrentIndex}
        onClose={closeLightbox}
      />

      {categorySelect.modal}
      {brandSelect.modal}
    </>
  );
};